export type ResultsOrderingMode = "fixed-then-sort" | "live";

// `live` is the shipped default: the shared comparator (`sortResults`) re-applies on every
// incremental `script-result` and rows animate to their new position via the `useFlipReorder` FLIP
// hook (reduced motion re-sorts instantly). `fixed-then-sort` remains a supported alternative: rows
// hold selection order for the whole run and are sorted exactly once, at `batch-completed`.
export const RESULTS_ORDERING_MODE: ResultsOrderingMode = "live";

// `maxScriptsPerBatch`/`maxContextKeysPerBatch` mirror the backend's own hard caps exactly
// (`script-runner.batch.max-scripts` / `.max-context-keys`, see co-services'
// `docs/script-runner/13-rest-api/06-configurable-limits.md` §13.8), so a chunk this config
// produces can never be rejected by the backend for exceeding either limit (`batchPool.ts`'s
// `packRules` enforces both, plus `maxRulesPerBatch`, as hard per-chunk caps).
//
// `maxConcurrentBatches: 1` makes every run strictly sequential (one chunk's batch/SSE stream at a
// time) — a deliberate product choice that also bounds the number of concurrent SSE connections a
// run can ever open, regardless of selection size.
//
// `maxRulesPerBatch: 256` is a product-chosen cap layered on top of the two backend limits above —
// it bounds how many rules/scriptIds/context keys a single chunk juggles even when a selection
// would otherwise fit comfortably under the backend caps.
export const MULTI_BATCH = {
  enabled: true,
  maxRulesPerBatch: 256,
  maxScriptsPerBatch: 1000,
  maxContextKeysPerBatch: 8192,
  maxConcurrentBatches: 1,
} as const;

// `live` mode re-applies the shared comparator on every incremental `onRowsChanged`, and each
// re-sort interrupts the previous re-sort's FLIP animation — a rule run that resolves many rows
// within a couple hundred milliseconds of each other would visibly thrash before settling. This
// config defers the actual re-sort until the stream goes quiet, so a fast run settles once instead
// of churning, while still reflecting current status+title severity continuously.
//
// `enabled: false` is a supported escape hatch that re-sorts immediately on every update, same as
// before coalescing existed. Scoped entirely to `live` — `fixed-then-sort` is unaffected.
//
// `quietWindowMs` is the inactivity window, reset by each new update; the deferred re-sort fires
// once the stream has been quiet this long. `maxHoldMs` is a ceiling from the first deferred update
// of the current window, never reset by a later update — without it, a steady trickle of results
// could defer the re-sort indefinitely.
//
// Both durations are plain, hand-tuned numbers rather than anything derived or computed.
export const RESULTS_REORDER_COALESCING = {
  enabled: true,
  quietWindowMs: 500,
  maxHoldMs: 2000,
} as const;

// A healthy run settles directly from `batch-completed`, without issuing `/join`; this silence
// window is the transport-independent backstop for a stream that never reaches that event. Twenty
// seconds is deliberately longer than the backend's 15-second keepalive interval. A plain,
// hand-tunable value, like the reorder timings above.
export const RUN_COMPLETION_WATCHDOG = {
  startJoinAfterSilenceMs: 20000,
} as const;
